import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import PrinterStatusCard from "@/components/PrinterStatusCard";

export default function Settings() {
    const { t, lang } = useApp();
    const [s, setS] = useState(null);

    useEffect(() => {
        api.get("/settings").then((r) => setS(r.data));
    }, []);

    if (!s) return <div className="text-muted-foreground">{t("loading")}</div>;

    const save = async () => {
        try {
            const r = await api.patch("/settings", {
                cafe_name_en: s.cafe_name_en,
                cafe_name_ar: s.cafe_name_ar,
                logo_url: s.logo_url,
                address: s.address,
                phone: s.phone,
                tax_percent: Number(s.tax_percent) || 0,
                receipt_footer_en: s.receipt_footer_en,
                receipt_footer_ar: s.receipt_footer_ar,
                kitchen_printer_enabled: s.kitchen_printer_enabled,
                currency: s.currency,
                delivery_fee: Number(s.delivery_fee) || 0,
                dine_in_enabled: s.dine_in_enabled !== false,
                pickup_enabled: s.pickup_enabled !== false,
                delivery_enabled: s.delivery_enabled !== false,
            });
            setS(r.data);
            toast.success(t("save"));
        } catch (e) {
            toast.error(e.response?.data?.detail || t("error"));
        }
    };

    return (
        <div className="space-y-4 max-w-3xl" data-testid="settings-page">
            <h1 className={`${lang === "ar" ? "font-ar" : "font-display-en italic"} text-3xl`}>{t("settings")}</h1>

            <Card className="grain bg-card/70">
                <CardContent className="p-5 space-y-4">
                    <div className="text-sm font-medium mb-2">{t("cafe_info")}</div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5"><Label>{t("cafe_name")} (EN)</Label><Input value={s.cafe_name_en || ""} onChange={(e) => setS({ ...s, cafe_name_en: e.target.value })} data-testid="setting-name-en" /></div>
                        <div className="space-y-1.5"><Label>{t("cafe_name")} (AR)</Label><Input dir="rtl" value={s.cafe_name_ar || ""} onChange={(e) => setS({ ...s, cafe_name_ar: e.target.value })} data-testid="setting-name-ar" /></div>
                    </div>
                    <div className="space-y-1.5"><Label>{t("logo_url")}</Label><Input value={s.logo_url || ""} onChange={(e) => setS({ ...s, logo_url: e.target.value })} data-testid="setting-logo" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5"><Label>{t("address")}</Label><Input value={s.address || ""} onChange={(e) => setS({ ...s, address: e.target.value })} /></div>
                        <div className="space-y-1.5"><Label>{t("phone")}</Label><Input value={s.phone || ""} onChange={(e) => setS({ ...s, phone: e.target.value })} /></div>
                    </div>
                </CardContent>
            </Card>

            <Card className="grain bg-card/70">
                <CardContent className="p-5 space-y-4">
                    <div className="text-sm font-medium mb-2">{t("print_receipt")}</div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5"><Label>{t("tax_percent")}</Label><Input type="number" value={s.tax_percent ?? 0} onChange={(e) => setS({ ...s, tax_percent: e.target.value })} data-testid="setting-tax" /></div>
                        <div className="space-y-1.5"><Label>{t("currency")}</Label><Input value={s.currency || "SDG"} onChange={(e) => setS({ ...s, currency: e.target.value })} /></div>
                    </div>
                    <div className="space-y-1.5"><Label>{t("receipt_footer")} (EN)</Label><Textarea rows={2} value={s.receipt_footer_en || ""} onChange={(e) => setS({ ...s, receipt_footer_en: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>{t("receipt_footer")} (AR)</Label><Textarea rows={2} dir="rtl" value={s.receipt_footer_ar || ""} onChange={(e) => setS({ ...s, receipt_footer_ar: e.target.value })} /></div>
                    <div className="flex items-center gap-3 pt-2">
                        <Switch checked={!!s.kitchen_printer_enabled} onCheckedChange={(v) => setS({ ...s, kitchen_printer_enabled: v })} data-testid="setting-kitchen-printer" />
                        <Label>{t("kitchen_printer")}</Label>
                    </div>
                </CardContent>
            </Card>

            <Card className="grain bg-card/70">
                <CardContent className="p-5 space-y-4">
                    <div className="text-sm font-medium mb-2">{t("ordering_channels")}</div>
                    <p className="text-xs text-muted-foreground -mt-2">{t("ordering_channels_desc")}</p>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between bg-secondary/40 rounded-xl px-4 py-3">
                            <Label className="flex flex-col gap-0.5">
                                <span className="font-medium">{t("dine_in")}</span>
                                <span className="text-[11px] text-muted-foreground">{t("dine_in_desc")}</span>
                            </Label>
                            <Switch checked={s.dine_in_enabled !== false} onCheckedChange={(v) => setS({ ...s, dine_in_enabled: v })} data-testid="setting-dine-in" />
                        </div>
                        <div className="flex items-center justify-between bg-secondary/40 rounded-xl px-4 py-3">
                            <Label className="flex flex-col gap-0.5">
                                <span className="font-medium">{t("pickup")}</span>
                                <span className="text-[11px] text-muted-foreground">{t("pickup_desc")}</span>
                            </Label>
                            <Switch checked={s.pickup_enabled !== false} onCheckedChange={(v) => setS({ ...s, pickup_enabled: v })} data-testid="setting-pickup" />
                        </div>
                        <div className="flex items-center justify-between bg-secondary/40 rounded-xl px-4 py-3">
                            <Label className="flex flex-col gap-0.5">
                                <span className="font-medium">{t("delivery")}</span>
                                <span className="text-[11px] text-muted-foreground">{t("delivery_desc")}</span>
                            </Label>
                            <Switch checked={s.delivery_enabled !== false} onCheckedChange={(v) => setS({ ...s, delivery_enabled: v })} data-testid="setting-delivery" />
                        </div>
                        <div className="space-y-1.5 pt-2">
                            <Label>{t("delivery_fee")}</Label>
                            <Input type="number" min="0" value={s.delivery_fee ?? 0} onChange={(e) => setS({ ...s, delivery_fee: e.target.value })} data-testid="setting-delivery-fee" />
                        </div>
                    </div>
                </CardContent>
            </Card>

            <div className="flex justify-end">
                <Button onClick={save} data-testid="save-settings-btn">{t("save")}</Button>
            </div>

            <PrinterStatusCard />
        </div>
    );
}
